import React, { useCallback, useState } from 'react';
import { PhoneCallItem } from '../../types';
import {
  fromEditModel,
  PhoneConvBranch,
  PhoneConvChoice,
  PhoneConvEditModel,
  PhoneConvMessage,
  PhoneConvNode,
  PhoneConversationItem,
  toEditModel,
} from './phone-conversation-adapter';
import { AssetPickerModal } from './AssetPickerModal';
import { assetUrl } from './scenario-api';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';
import type { InlineScenarioChangeOptions } from './use-inline-block-history';
import { resolvePhoneCallStyle } from '../../systems/phone-call-style';

interface InlinePhoneContactMetaProps {
  contactName: string;
  contactAvatar?: string;
  contactRole?: string;
  showRole?: boolean;
  scenarioId: string;
  onChange: (patch: { contactName?: string; contactAvatar?: string; contactRole?: string }) => void;
}

function InlinePhoneContactMeta({
  contactName,
  contactAvatar,
  contactRole,
  showRole = false,
  scenarioId,
  onChange,
}: InlinePhoneContactMetaProps) {
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);

  return (
    <div className={inline.phoneConvContactRow}>
      {contactAvatar ? (
        <img
          className={inline.phoneConvContactAvatarThumb}
          src={assetUrl(scenarioId, contactAvatar)}
          alt=""
        />
      ) : (
        <div className={inline.phoneConvContactAvatarPlaceholder} aria-hidden />
      )}

      <div className={`${inline.metaField} ${inline.phoneConvContactNameField}`}>
        <span className={inline.metaFieldLabel}>Nom du contact</span>
        <input
          className={studio.input}
          value={contactName}
          onChange={(e) => onChange({ contactName: e.target.value })}
          placeholder="Kevin — Ministère"
        />
      </div>

      {showRole && (
        <div className={`${inline.metaField} ${inline.phoneConvContactNameField}`}>
          <span className={inline.metaFieldLabel}>Rôle / fonction</span>
          <input
            className={studio.input}
            value={contactRole ?? ''}
            onChange={(e) => onChange({ contactRole: e.target.value || undefined })}
            placeholder="La Directrice"
          />
        </div>
      )}

      <div className={`${inline.metaField} ${inline.phoneConvContactAvatarField}`}>
        <span className={inline.metaFieldLabel}>Avatar</span>
        <div className={inline.phoneConvContactAvatarInputRow}>
          <input
            className={studio.input}
            value={contactAvatar ?? ''}
            onChange={(e) => onChange({ contactAvatar: e.target.value || undefined })}
            placeholder="assets/characters/…"
          />
          <button
            type="button"
            className={studio.btnSecondary}
            onClick={() => setAvatarPickerOpen(true)}
          >
            Parcourir
          </button>
        </div>
      </div>

      {avatarPickerOpen && (
        <AssetPickerModal
          scenarioId={scenarioId}
          value={contactAvatar ?? ''}
          subdir="characters"
          onSelect={(path) => {
            onChange({ contactAvatar: path });
            setAvatarPickerOpen(false);
          }}
          onClose={() => setAvatarPickerOpen(false)}
        />
      )}
    </div>
  );
}

function EditableText({
  value,
  className,
  onChange,
  placeholder,
}: {
  value: string;
  className?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLSpanElement>) => {
      onChange(e.currentTarget.textContent?.trim() ?? '');
    },
    [onChange]
  );

  return (
    <span
      className={className}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
    >
      {value || placeholder || ''}
    </span>
  );
}

function getChoiceAtPath(model: PhoneConvEditModel, path: number[]): PhoneConvChoice | null {
  if (!model.choice) return null;
  if (path.length === 0) return model.choice;

  let current = model.choice;
  for (const optionIndex of path) {
    const branch = current.options[optionIndex];
    if (!branch) return null;
    const choiceNode = branch.nodes.find((n): n is PhoneConvChoice => n.kind === 'choice');
    if (!choiceNode) return null;
    current = choiceNode;
  }
  return current;
}

function setChoiceAtPath(
  model: PhoneConvEditModel,
  path: number[],
  choice: PhoneConvChoice | null
): PhoneConvEditModel {
  if (path.length === 0) {
    return { ...model, choice };
  }

  if (!model.choice) return model;

  const updateBranchNodes = (
    nodes: PhoneConvNode[],
    rest: number[],
    nextChoice: PhoneConvChoice | null
  ): PhoneConvNode[] => {
    const [head, ...tail] = rest;
    return nodes.map((node) => {
      if (node.kind !== 'choice') return node;
      if (tail.length === 0) {
        return {
          ...node,
          options: node.options.map((branch, i) =>
            i === head
              ? {
                  ...branch,
                  nodes: nextChoice
                    ? upsertChoiceNode(branch.nodes, nextChoice)
                    : removeChoiceNode(branch.nodes),
                }
              : branch
          ),
        };
      }
      return {
        ...node,
        options: node.options.map((branch, i) =>
          i === head ? { ...branch, nodes: updateBranchNodes(branch.nodes, tail, nextChoice) } : branch
        ),
      };
    });
  };

  const [head, ...tail] = path;
  if (tail.length === 0) {
    return {
      ...model,
      choice: {
        ...model.choice,
        options: model.choice.options.map((branch, i) =>
          i === head
            ? {
                ...branch,
                nodes: choice
                  ? upsertChoiceNode(branch.nodes, choice)
                  : removeChoiceNode(branch.nodes),
              }
            : branch
        ),
      },
    };
  }

  return {
    ...model,
    choice: {
      ...model.choice,
      options: model.choice.options.map((branch, i) =>
        i === head ? { ...branch, nodes: updateBranchNodes(branch.nodes, tail, choice) } : branch
      ),
    },
  };
}

function upsertChoiceNode(nodes: PhoneConvNode[], choice: PhoneConvChoice): PhoneConvNode[] {
  const without = nodes.filter((n) => n.kind !== 'choice');
  return [...without, choice];
}

function removeChoiceNode(nodes: PhoneConvNode[]): PhoneConvNode[] {
  return nodes.filter((n) => n.kind !== 'choice');
}

function getBranchAtPath(model: PhoneConvEditModel, choicePath: number[], optionIndex: number): PhoneConvBranch | null {
  const choice = getChoiceAtPath(model, choicePath);
  return choice?.options[optionIndex] ?? null;
}

function updateBranchAtPath(
  model: PhoneConvEditModel,
  choicePath: number[],
  optionIndex: number,
  patch: Partial<PhoneConvBranch>
): PhoneConvEditModel {
  const choice = getChoiceAtPath(model, choicePath);
  if (!choice) return model;

  const nextChoice: PhoneConvChoice = {
    ...choice,
    options: choice.options.map((branch, i) =>
      i === optionIndex ? { ...branch, ...patch } : branch
    ),
  };

  return setChoiceAtPath(model, choicePath, nextChoice);
}

function setBranchContactReply(
  model: PhoneConvEditModel,
  choicePath: number[],
  optionIndex: number,
  text: string
): PhoneConvEditModel {
  const branch = getBranchAtPath(model, choicePath, optionIndex);
  if (!branch) return model;

  const trimmed = text.trim();
  const otherNodes = branch.nodes.filter((n) => n.kind !== 'message' || n.sender !== 'contact');
  const nodes: PhoneConvNode[] = trimmed
    ? [{ kind: 'message', sender: 'contact', text: trimmed }, ...otherNodes]
    : otherNodes;

  return updateBranchAtPath(model, choicePath, optionIndex, { nodes });
}

function moveOptionAtPath(
  model: PhoneConvEditModel,
  choicePath: number[],
  optionIndex: number,
  dir: -1 | 1
): PhoneConvEditModel {
  const choice = getChoiceAtPath(model, choicePath);
  if (!choice) return model;

  const target = optionIndex + dir;
  if (target < 0 || target >= choice.options.length) return model;

  const options = [...choice.options];
  [options[optionIndex], options[target]] = [options[target]!, options[optionIndex]!];
  return setChoiceAtPath(model, choicePath, { ...choice, options });
}

function insertOptionAtPath(
  model: PhoneConvEditModel,
  choicePath: number[],
  afterIndex: number,
  label: string
): PhoneConvEditModel {
  const choice = getChoiceAtPath(model, choicePath);
  const options = [...(choice?.options ?? [])];
  options.splice(afterIndex + 1, 0, { label, nodes: [] });
  const nextChoice: PhoneConvChoice = { kind: 'choice', options };
  return setChoiceAtPath(model, choicePath, nextChoice);
}

function removeOptionAtPath(
  model: PhoneConvEditModel,
  choicePath: number[],
  optionIndex: number
): PhoneConvEditModel {
  const choice = getChoiceAtPath(model, choicePath);
  if (!choice) return model;

  const nextOptions = choice.options.filter((_, i) => i !== optionIndex);
  if (nextOptions.length === 0) {
    return setChoiceAtPath(model, choicePath, null);
  }

  return setChoiceAtPath(model, choicePath, { ...choice, options: nextOptions });
}

function addNestedChoice(
  model: PhoneConvEditModel,
  choicePath: number[],
  optionIndex: number
): PhoneConvEditModel {
  const branch = getBranchAtPath(model, choicePath, optionIndex);
  if (!branch) return model;

  const nested: PhoneConvChoice = {
    kind: 'choice',
    options: [{ label: 'Nouvelle réponse', nodes: [] }],
  };
  return updateBranchAtPath(model, choicePath, optionIndex, {
    nodes: upsertChoiceNode(branch.nodes, nested),
  });
}

interface ChoiceLevelProps {
  model: PhoneConvEditModel;
  choicePath: number[];
  depth: number;
  isSms: boolean;
  onModelChange: (model: PhoneConvEditModel, recordHistory?: boolean) => void;
}

function ChoiceLevel({ model, choicePath, depth, isSms, onModelChange }: ChoiceLevelProps) {
  const choice = getChoiceAtPath(model, choicePath);
  if (!choice) return null;

  return (
    <div className={depth > 0 ? inline.phoneConvNested : inline.phoneConvChoiceSection}>
      {depth > 0 && <span className={inline.phoneConvSectionLabel}>Sous-choix</span>}
      {choice.options.map((branch, optionIndex) => {
        const contactNode = branch.nodes.find(
          (n): n is PhoneConvMessage => n.kind === 'message' && n.sender === 'contact'
        );
        const nestedChoice = branch.nodes.find((n): n is PhoneConvChoice => n.kind === 'choice');
        const nestedPath = [...choicePath, optionIndex];

        return (
          <div key={`${choicePath.join('-')}-${optionIndex}`} className={inline.choiceOptionBlock}>
            <div className={inline.choiceOptionRow}>
              <span className={inline.choiceOptionIndex} aria-hidden>
                {depth > 0 ? '↳' : '▶'}
              </span>
              <EditableText
                value={branch.label}
                className={inline.choiceOptionText}
                onChange={(label) =>
                  onModelChange(updateBranchAtPath(model, choicePath, optionIndex, { label }))
                }
                placeholder="Réponse du joueur…"
              />
              <button
                type="button"
                className={inline.choiceOptionMove}
                onClick={() =>
                  onModelChange(moveOptionAtPath(model, choicePath, optionIndex, -1), true)
                }
                disabled={optionIndex === 0}
                title="Monter"
              >
                ↑
              </button>
              <button
                type="button"
                className={inline.choiceOptionMove}
                onClick={() =>
                  onModelChange(moveOptionAtPath(model, choicePath, optionIndex, 1), true)
                }
                disabled={optionIndex === choice.options.length - 1}
                title="Descendre"
              >
                ↓
              </button>
              <button
                type="button"
                className={inline.choiceOptionAdd}
                onClick={() =>
                  onModelChange(
                    insertOptionAtPath(
                      model,
                      choicePath,
                      optionIndex,
                      depth === 0 ? 'Nouvelle réponse' : '…'
                    ),
                    true
                  )
                }
                title={depth > 0 ? 'Ajouter une sous-réponse' : 'Ajouter une réponse'}
              >
                +
              </button>
              <button
                type="button"
                className={inline.choiceOptionRemove}
                onClick={() =>
                  onModelChange(removeOptionAtPath(model, choicePath, optionIndex), true)
                }
                title={depth > 0 ? 'Supprimer cette sous-réponse' : 'Supprimer cette réponse'}
              >
                ×
              </button>
            </div>
            <div className={inline.metaField}>
              <span className={inline.metaFieldLabel}>Réplique du contact</span>
              <EditableText
                value={contactNode?.text ?? ''}
                className={inline.slText}
                onChange={(text) =>
                  onModelChange(setBranchContactReply(model, choicePath, optionIndex, text))
                }
                placeholder="Ce que dit le contact après ce choix…"
              />
            </div>
            {nestedChoice ? (
              <ChoiceLevel
                model={model}
                choicePath={nestedPath}
                depth={depth + 1}
                isSms={isSms}
                onModelChange={onModelChange}
              />
            ) : (
              <button
                type="button"
                className={`${studio.btnGhost} ${inline.phoneConvAddBtn}`}
                onClick={() =>
                  onModelChange(addNestedChoice(model, choicePath, optionIndex), true)
                }
              >
                + Sous-choix
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface PreambleEditorProps {
  preamble: PhoneConvMessage[];
  isSms: boolean;
  onChange: (preamble: PhoneConvMessage[], recordHistory?: boolean) => void;
}

function PreambleEditor({ preamble, isSms, onChange }: PreambleEditorProps) {
  const updateMessage = (index: number, patch: Partial<PhoneConvMessage>) => {
    onChange(preamble.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const removeMessage = (index: number) => {
    onChange(
      preamble.filter((_, i) => i !== index),
      true
    );
  };

  const moveMessage = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= preamble.length) return;
    const copy = [...preamble];
    [copy[index], copy[next]] = [copy[next]!, copy[index]!];
    onChange(copy, true);
  };

  const addMessage = (sender: 'contact' | 'player') => {
    onChange([...preamble, { kind: 'message', sender, text: '' }], true);
  };

  return (
    <div className={inline.phoneConvPreamble}>
      <span className={inline.phoneConvSectionLabel}>Préambule</span>
      {preamble.map((msg, i) => (
        <div
          key={i}
          className={`${inline.phoneConvBubble} ${
            msg.sender === 'player' ? inline.phoneConvBubblePlayer : inline.phoneConvBubbleContact
          }`}
        >
          <div className={inline.phoneConvBubbleHeader}>
            <span className={inline.phoneConvBubbleSender}>
              {msg.sender === 'player' ? 'Joueur' : 'Contact'}
              {isSms && msg.sender === 'contact' && (
                <>
                  {' · '}
                  <EditableText
                    value={msg.time ?? ''}
                    className={inline.phoneConvTime}
                    onChange={(time) => updateMessage(i, { time: time || undefined })}
                    placeholder="08:00"
                  />
                </>
              )}
            </span>
            <div className={inline.phoneConvBubbleActions}>
              <button
                type="button"
                className={inline.phoneConvBubbleBtn}
                onClick={() => moveMessage(i, -1)}
                disabled={i === 0}
                title="Monter"
              >
                ↑
              </button>
              <button
                type="button"
                className={inline.phoneConvBubbleBtn}
                onClick={() => moveMessage(i, 1)}
                disabled={i === preamble.length - 1}
                title="Descendre"
              >
                ↓
              </button>
              <button
                type="button"
                className={inline.phoneConvBubbleBtn}
                onClick={() => removeMessage(i)}
                title="Supprimer"
              >
                ×
              </button>
            </div>
          </div>
          <EditableText
            value={msg.text}
            className={inline.phoneConvBubbleText}
            onChange={(text) => updateMessage(i, { text })}
            placeholder="Réplique…"
          />
        </div>
      ))}
      <div className={inline.phoneConvPreambleActions}>
        <button type="button" className={studio.btnSecondary} onClick={() => addMessage('contact')}>
          + Contact
        </button>
        <button type="button" className={studio.btnSecondary} onClick={() => addMessage('player')}>
          + Joueur (auto)
        </button>
      </div>
    </div>
  );
}

function ReadonlyBranch({ branch, depth }: { branch: PhoneConvBranch; depth: number }) {
  const contactNode = branch.nodes.find(
    (n): n is PhoneConvMessage => n.kind === 'message' && n.sender === 'contact'
  );
  const nestedChoice = branch.nodes.find((n): n is PhoneConvChoice => n.kind === 'choice');

  return (
    <div className={depth > 0 ? inline.phoneConvNested : undefined}>
      <span className={inline.choiceBtn}>
        <span className={inline.choiceBtnLabel} aria-hidden>
          {depth > 0 ? '↳' : '▶'}
        </span>
        <span className={inline.choiceBtnLabel}>{branch.label || '…'}</span>
        {contactNode?.text && (
          <span className={inline.choiceDest}>← {contactNode.text}</span>
        )}
      </span>
      {nestedChoice?.options.map((child, i) => (
        <ReadonlyBranch key={i} branch={child} depth={depth + 1} />
      ))}
    </div>
  );
}

export function InlinePhoneConversationReadonly({ item }: { item: PhoneConversationItem }) {
  const model = toEditModel(item);
  const isSms = item.type === 'phoneChat';

  return (
    <div className={inline.phoneConvReadonly}>
      {model.preamble.map((msg, i) => (
        <div
          key={i}
          className={`${inline.phoneConvBubbleReadonly} ${
            msg.sender === 'player'
              ? inline.phoneConvBubblePlayer
              : inline.phoneConvBubbleContact
          }`}
        >
          <span className={inline.phoneConvBubbleSender}>
            {msg.sender === 'player' ? 'Joueur' : 'Contact'}
            {isSms && msg.time ? ` · ${msg.time}` : ''}
          </span>
          <span>{msg.text || '…'}</span>
        </div>
      ))}
      {model.choice && (
        <div className={inline.phoneConvChoiceReadonly}>
          {model.choice.options.map((branch, i) => (
            <ReadonlyBranch key={i} branch={branch} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}

interface InlinePhoneConversationEditorProps {
  item: PhoneConversationItem;
  scenarioId: string;
  convergeSceneId?: string;
  onChange: (item: PhoneConversationItem, options?: InlineScenarioChangeOptions) => void;
}

export const InlinePhoneConversationEditor: React.FC<InlinePhoneConversationEditorProps> = ({
  item,
  scenarioId,
  convergeSceneId,
  onChange,
}) => {
  const model = toEditModel(item);
  const isSms = item.type === 'phoneChat';
  const isCall = item.type === 'phoneCall';
  const callStyle = isCall ? resolvePhoneCallStyle(item as PhoneCallItem) : 'inline_call';
  const isPhoneStyle = callStyle === 'phone';

  const applyModel = (nextModel: PhoneConvEditModel, recordHistory = false) => {
    onChange(fromEditModel(item, nextModel, convergeSceneId), recordHistory ? { recordBlockHistory: true } : undefined);
  };

  const patchItem = (patch: Partial<PhoneConversationItem>) => {
    onChange({ ...item, ...patch });
  };

  const addRootChoice = () => {
    applyModel(
      {
        ...model,
        choice: {
          kind: 'choice',
          options: [{ label: 'Nouvelle réponse', nodes: [] }],
        },
      },
      true
    );
  };

  const removeRootChoice = () => {
    applyModel({ ...model, choice: null }, true);
  };

  return (
    <div className={inline.phoneConvEditor}>
      <InlinePhoneContactMeta
        contactName={item.contactName}
        contactAvatar={item.contactAvatar}
        contactRole={isCall ? (item as PhoneCallItem).contactRole : undefined}
        showRole={isCall && !isPhoneStyle}
        scenarioId={scenarioId}
        onChange={patchItem}
      />

      {isCall && (
        <div className={inline.phoneConvCallSettings}>
          <div className={`${inline.metaField} ${inline.phoneConvContactNameField}`}>
            <span className={inline.metaFieldLabel}>Style</span>
            <select
              className={studio.select}
              value={callStyle}
              onChange={(e) =>
                patchItem({
                  style: e.target.value as PhoneCallItem['style'],
                } as Partial<PhoneCallItem>)
              }
            >
              <option value="inline_call">Appel overlay</option>
              <option value="phone">Téléphone</option>
            </select>
          </div>
          {!isPhoneStyle && (
            <label className={inline.phoneConvCheckbox}>
              <input
                type="checkbox"
                checked={Boolean((item as PhoneCallItem).incomingCall)}
                onChange={(e) => patchItem({ incomingCall: e.target.checked } as Partial<PhoneCallItem>)}
              />
              Appel entrant
            </label>
          )}
          {isPhoneStyle && (
            <>
              <label className={inline.phoneConvCheckbox}>
                <input
                  type="checkbox"
                  checked={Boolean((item as PhoneCallItem).incomingCall)}
                  onChange={(e) => patchItem({ incomingCall: e.target.checked } as Partial<PhoneCallItem>)}
                />
                Appel entrant
              </label>
              <label className={inline.phoneConvCheckbox}>
                <input
                  type="checkbox"
                  checked={Boolean((item as PhoneCallItem).autoAnswer)}
                  onChange={(e) => patchItem({ autoAnswer: e.target.checked } as Partial<PhoneCallItem>)}
                  disabled={!(item as PhoneCallItem).incomingCall}
                />
                Réponse auto
              </label>
              <label className={inline.phoneConvCheckbox}>
                <input
                  type="checkbox"
                  checked={Boolean((item as PhoneCallItem).allowDecline)}
                  onChange={(e) => patchItem({ allowDecline: e.target.checked } as Partial<PhoneCallItem>)}
                  disabled={!(item as PhoneCallItem).incomingCall}
                />
                Refus possible
              </label>
            </>
          )}
        </div>
      )}

      <PreambleEditor
        preamble={model.preamble}
        isSms={isSms}
        onChange={(preamble, recordHistory) =>
          applyModel({ ...model, preamble }, recordHistory)
        }
      />

      {model.choice ? (
        <>
          <div className={inline.phoneConvChoiceHeader}>
            <span className={inline.phoneConvSectionLabel}>Choix de discussion</span>
            <button type="button" className={studio.btnGhost} onClick={removeRootChoice}>
              Retirer les choix
            </button>
          </div>
          <ChoiceLevel
            model={model}
            choicePath={[]}
            depth={0}
            isSms={isSms}
            onModelChange={applyModel}
          />
        </>
      ) : (
        <button
          type="button"
          className={`${studio.btnSecondary} ${inline.phoneConvAddBtn}`}
          onClick={addRootChoice}
        >
          + Choix de discussion
        </button>
      )}
    </div>
  );
};
